import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Check, X, ChevronLeft, ChevronRight, Loader2, Upload, Plus,
  CheckCircle2, XCircle, ArrowRight, Trash2, AlertCircle, Save,
} from 'lucide-react';

const API = '/api'; // Same-origin: /api routes on the serving domain (Preview or Production custom domain) to backend via Emergent ingress. Avoids cross-domain CORS / tracking-prevention failures.

function Stat({ label, value, color = '#5C5C5C', testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-hg-border rounded-sm p-3 text-center">
      <div className="text-[10px] font-sans text-hg-text-dim uppercase tracking-[0.18em]">{label}</div>
      <div className="font-display text-2xl mt-1" style={{ color }}>{(value || 0).toLocaleString()}</div>
    </div>
  );
}

function NewListModal({ onClose, onCreated }) {
  const [csvUrl, setCsvUrl] = useState('');
  const [csvText, setCsvText] = useState('');
  const [listName, setListName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!listName.trim()) return setErr('List name required');
    if (!csvUrl.trim() && !csvText.trim()) return setErr('Provide a CSV URL or paste CSV text');
    setBusy(true); setErr('');
    try {
      const res = await axios.post(`${API}/people/csv-import`, {
        csv_url: csvUrl.trim() || null,
        csv_text: csvText.trim() || null,
        list_name: listName.trim(),
      });
      onCreated(listName.trim(), res.data.imported);
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Import failed');
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-hg-border rounded-sm w-full max-w-lg p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="new-csv-list-modal">
        <h3 className="font-display text-lg tracking-wider">START NEW CURATION LIST</h3>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">List Name</label>
            <input
              data-testid="curate-list-name"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              placeholder="e.g. Clean Fitter List"
              className="w-full bg-white border border-hg-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-hg-green"
            />
          </div>
          <div>
            <label className="text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">CSV URL (optional)</label>
            <input
              data-testid="curate-csv-url"
              value={csvUrl}
              onChange={(e) => setCsvUrl(e.target.value)}
              placeholder="https://...your-export.csv"
              className="w-full bg-white border border-hg-border rounded-sm px-3 py-2 text-xs font-mono focus:outline-none focus:border-hg-green"
            />
          </div>
          <div className="text-center text-[10px] text-hg-text-dim">OR</div>
          <div>
            <label className="text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">Paste CSV Text</label>
            <textarea
              data-testid="curate-csv-text"
              value={csvText}
              onChange={(e) => setCsvText(e.target.value)}
              placeholder="Paste CSV content here (must have headers row including Email Address, First Name, Last Name)"
              rows={5}
              className="w-full bg-white border border-hg-border rounded-sm px-3 py-2 text-[11px] font-mono focus:outline-none focus:border-hg-green"
            />
          </div>
          {err && <div className="text-xs text-red-600 font-sans flex items-center gap-1"><AlertCircle size={12} />{err}</div>}
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose} className="px-3 py-2 text-xs border border-hg-border rounded-sm">Cancel</button>
          <button data-testid="curate-import-submit" onClick={submit} disabled={busy} className="px-3 py-2 text-xs bg-hg-green text-white rounded-sm disabled:opacity-40 flex items-center gap-1.5">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
            Import
          </button>
        </div>
      </div>
    </div>
  );
}

function FinalizeModal({ listName, includedCount, onClose, onDone }) {
  const [groupName, setGroupName] = useState(listName);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (!groupName.trim()) return setErr('Group name required');
    setBusy(true); setErr('');
    try {
      const res = await axios.post(`${API}/people/csv-candidates/finalize`, {
        list_name: listName,
        mailerlite_group_name: groupName.trim(),
      });
      onDone(res.data);
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Finalize failed');
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-hg-border rounded-sm w-full max-w-md p-6 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="curate-finalize-modal">
        <h3 className="font-display text-lg tracking-wider">CREATE MAILERLITE GROUP</h3>
        <p className="text-sm text-hg-text-dim">Push the {includedCount} included people into a new Mailerlite group, ready to send to.</p>
        <div>
          <label className="text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">Group Name</label>
          <input
            data-testid="finalize-group-name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            className="w-full bg-white border border-hg-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-hg-green"
          />
        </div>
        {err && <div className="text-xs text-red-600 font-sans">{err}</div>}
        <div className="flex gap-2 justify-end pt-2">
          <button onClick={onClose} className="px-3 py-2 text-xs border border-hg-border rounded-sm">Cancel</button>
          <button data-testid="finalize-submit" onClick={submit} disabled={busy} className="px-3 py-2 text-xs bg-hg-green text-white rounded-sm disabled:opacity-40 flex items-center gap-1.5">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            Create & Push
          </button>
        </div>
      </div>
    </div>
  );
}

export default function CurateList() {
  const [lists, setLists] = useState([]);
  const [activeList, setActiveList] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [stats, setStats] = useState({ total: 0, pending: 0, included: 0, skipped: 0 });
  const [cursorIdx, setCursorIdx] = useState(0);  // index into pending queue
  const [loading, setLoading] = useState(false);
  const [showNewList, setShowNewList] = useState(false);
  const [showFinalize, setShowFinalize] = useState(false);
  const [editFirst, setEditFirst] = useState('');
  const [editLast, setEditLast] = useState('');
  const [filter, setFilter] = useState('pending');  // pending | all | include | skip
  const [toast, setToast] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);

  const loadLists = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/people/csv-lists`);
      setLists(r.data.lists || []);
      if (!activeList && r.data.lists?.length) setActiveList(r.data.lists[0].name);
    } catch (e) { /* */ }
  }, [activeList]);

  const loadCandidate = useCallback(async (listName, idx) => {
    setLoading(true);
    try {
      const params = { list_name: listName, cursor_idx: idx, per_page: 1 };
      if (filter !== 'all') params.decision = filter;
      const r = await axios.get(`${API}/people/csv-candidates`, { params });
      setCandidate(r.data.items?.[0] || null);
      setStats(r.data.stats || { total: 0, pending: 0, included: 0, skipped: 0 });
      setEditFirst(r.data.items?.[0]?.first_name || '');
      setEditLast(r.data.items?.[0]?.last_name || '');
    } catch (e) { /* */ }
    setLoading(false);
  }, [filter]);

  useEffect(() => { loadLists(); }, [loadLists]);
  useEffect(() => {
    if (activeList) { setCursorIdx(0); loadCandidate(activeList, 0); }
  }, [activeList, filter, loadCandidate]);

  const decide = async (decision) => {
    if (!candidate) return;
    try {
      await axios.put(`${API}/people/csv-candidates/${candidate.id}`, {
        decision,
        first_name: editFirst,
        last_name: editLast,
      });
      // For 'pending' filter, decisions remove the item from the queue, so stay at same idx
      // For other filters, advance idx
      const nextIdx = filter === 'pending' ? cursorIdx : cursorIdx + 1;
      setCursorIdx(nextIdx);
      await loadCandidate(activeList, nextIdx);
      await loadLists();
    } catch (e) { /* */ }
  };

  const go = (delta) => {
    const ni = Math.max(0, cursorIdx + delta);
    setCursorIdx(ni);
    loadCandidate(activeList, ni);
  };

  const includeAll = async () => {
    if (!confirm(`Mark ALL ${stats.pending} pending candidates as INCLUDE?`)) return;
    setBulkBusy(true);
    try {
      await axios.post(`${API}/people/csv-candidates/bulk-decide`, {
        list_name: activeList,
        decision: 'include',
        only_pending: true,
      });
      await loadCandidate(activeList, 0);
      setCursorIdx(0);
      await loadLists();
      setToast('All pending marked include');
      setTimeout(() => setToast(''), 3000);
    } catch (e) { /* */ }
    setBulkBusy(false);
  };

  const skipAll = async () => {
    if (!confirm(`Mark ALL ${stats.pending} pending candidates as SKIP?`)) return;
    setBulkBusy(true);
    try {
      await axios.post(`${API}/people/csv-candidates/bulk-decide`, {
        list_name: activeList,
        decision: 'skip',
        only_pending: true,
      });
      await loadCandidate(activeList, 0);
      setCursorIdx(0);
      await loadLists();
      setToast('All pending marked skip');
      setTimeout(() => setToast(''), 3000);
    } catch (e) { /* */ }
    setBulkBusy(false);
  };

  const discardList = async () => {
    if (!confirm(`Permanently discard the curation list "${activeList}"? (Mailerlite is not affected.)`)) return;
    try {
      await axios.delete(`${API}/people/csv-candidates`, { params: { list_name: activeList } });
      setActiveList(null);
      setCandidate(null);
      await loadLists();
    } catch (e) { /* */ }
  };

  if (lists.length === 0) {
    return (
      <div className="space-y-4" data-testid="curate-empty">
        {showNewList && <NewListModal onClose={() => setShowNewList(false)} onCreated={(n) => { setActiveList(n); loadLists(); }} />}
        <div className="bg-white border border-hg-border rounded-sm p-10 text-center space-y-4">
          <Upload size={28} className="mx-auto text-hg-text-dim" strokeWidth={1.5} />
          <div>
            <h3 className="font-display text-lg tracking-wider">CURATE A NEW LIST</h3>
            <p className="text-sm text-hg-text-dim mt-1 max-w-md mx-auto">Import a CSV (Mailchimp export, etc.) and review each contact one-by-one before adding them to a clean Mailerlite group.</p>
          </div>
          <button data-testid="curate-start-new" onClick={() => setShowNewList(true)} className="px-4 py-2 text-sm bg-hg-green text-white rounded-sm hover:bg-hg-green-hover inline-flex items-center gap-2">
            <Plus size={14} /> Start New List
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="curate-list">
      {showNewList && <NewListModal onClose={() => setShowNewList(false)} onCreated={(n) => { setActiveList(n); loadLists(); }} />}
      {showFinalize && <FinalizeModal listName={activeList} includedCount={stats.included} onClose={() => setShowFinalize(false)} onDone={(res) => { setToast(`Created Mailerlite group with ${res.pushed} people`); setTimeout(() => setToast(''), 5000); }} />}

      {toast && <div className="fixed top-4 right-4 z-50 bg-hg-green/10 border border-hg-green/30 text-hg-green px-4 py-2 rounded-sm text-sm flex items-center gap-2"><Check size={14} />{toast}</div>}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-xl tracking-wider">CURATE LIST</h2>
          <p className="text-xs text-hg-text-dim mt-1">Review each person, mark Include or Skip, then create a clean Mailerlite group.</p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="curate-list-picker" value={activeList || ''} onChange={(e) => setActiveList(e.target.value)} className="bg-white border border-hg-border rounded-sm px-3 py-1.5 text-xs focus:outline-none focus:border-hg-green">
            {lists.map((l) => <option key={l.name} value={l.name}>{l.name} · {l.pending} pending</option>)}
          </select>
          <button data-testid="curate-new-list-btn" onClick={() => setShowNewList(true)} className="px-3 py-1.5 text-xs border border-hg-border rounded-sm hover:border-hg-green hover:text-hg-green inline-flex items-center gap-1.5">
            <Plus size={11} /> New
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-2">
        <Stat label="Total" value={stats.total} testid="curate-stat-total" />
        <Stat label="Pending" value={stats.pending} color="#8C6534" testid="curate-stat-pending" />
        <Stat label="Included" value={stats.included} color="#2F8A33" testid="curate-stat-included" />
        <Stat label="Skipped" value={stats.skipped} color="#C03434" testid="curate-stat-skipped" />
      </div>

      {/* Filter + bulk actions */}
      <div className="flex items-center gap-1 border-b border-hg-border">
        {[
          { id: 'pending', label: `Pending (${stats.pending})` },
          { id: 'include', label: `Included (${stats.included})` },
          { id: 'skip', label: `Skipped (${stats.skipped})` },
          { id: 'all', label: 'All' },
        ].map((f) => (
          <button
            key={f.id}
            data-testid={`curate-filter-${f.id}`}
            onClick={() => setFilter(f.id)}
            className={`px-3 py-2 text-xs uppercase tracking-[0.18em] border-b-2 ${filter === f.id ? 'border-hg-green text-hg-green' : 'border-transparent text-hg-text-dim hover:text-hg-text'}`}
          >{f.label}</button>
        ))}
        <div className="flex-1" />
        {stats.pending > 0 && (
          <>
            <button data-testid="curate-include-all" disabled={bulkBusy} onClick={includeAll} className="text-[10px] text-hg-green hover:underline px-2 py-1 disabled:opacity-40">Include all pending</button>
            <button data-testid="curate-skip-all" disabled={bulkBusy} onClick={skipAll} className="text-[10px] text-red-600 hover:underline px-2 py-1 disabled:opacity-40">Skip all pending</button>
          </>
        )}
        <button data-testid="curate-discard-list" onClick={discardList} className="text-[10px] text-hg-text-dim hover:text-red-600 px-2 py-1"><Trash2 size={10} className="inline" /> Discard list</button>
      </div>

      {/* Candidate card */}
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 size={22} className="animate-spin text-hg-green" /></div>
      ) : !candidate ? (
        <div className="bg-white border border-hg-border rounded-sm p-10 text-center space-y-4">
          <CheckCircle2 size={36} className="mx-auto text-hg-green" strokeWidth={1.5} />
          <div>
            <h3 className="font-display text-lg tracking-wider">DONE WITH THIS BATCH</h3>
            <p className="text-sm text-hg-text-dim mt-1">
              {filter === 'pending' && stats.pending === 0 ? `All ${stats.total} candidates reviewed.` : 'No matching candidates for this filter.'}
            </p>
          </div>
          {stats.included > 0 && (
            <button data-testid="curate-finalize-btn" onClick={() => setShowFinalize(true)} className="px-4 py-2 text-sm bg-hg-green text-white rounded-sm hover:bg-hg-green-hover inline-flex items-center gap-2">
              Create Mailerlite Group with {stats.included} People <ArrowRight size={14} />
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white border border-hg-border rounded-sm p-6 sm:p-8 space-y-5" data-testid="curate-candidate-card">
          {/* Position indicator */}
          <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-hg-text-dim font-sans">
            <span>Reviewing person {cursorIdx + 1}</span>
            <span className={`px-2 py-0.5 rounded-sm ${candidate.decision === 'include' ? 'bg-hg-green/10 text-hg-green' : candidate.decision === 'skip' ? 'bg-red-100 text-red-700' : 'bg-hg-surface-hover text-hg-text-dim'}`}>
              {candidate.decision}
            </span>
          </div>

          {/* Person info */}
          <div className="text-center space-y-3 py-3">
            <div className="font-display text-2xl tracking-wider">
              {(candidate.first_name || candidate.last_name) ? `${candidate.first_name} ${candidate.last_name}`.trim() : <span className="text-hg-text-dim italic text-base">(no name)</span>}
            </div>
            <div className="text-sm text-hg-text-dim font-mono">{candidate.email}</div>
            {(candidate.phone || candidate.notes || candidate.tags_source) && (
              <div className="text-xs text-hg-text-dim font-sans space-y-1 pt-2">
                {candidate.phone && <div>📞 {candidate.phone}</div>}
                {candidate.tags_source && <div>Tags: {candidate.tags_source}</div>}
                {candidate.notes && <div className="italic">"{candidate.notes}"</div>}
              </div>
            )}
          </div>

          {/* Inline name edit */}
          <div className="bg-hg-surface-hover/50 border border-hg-border rounded-sm p-3 space-y-2">
            <div className="text-[10px] font-sans text-hg-text-dim uppercase tracking-wider">Edit name before deciding (optional)</div>
            <div className="grid grid-cols-2 gap-2">
              <input
                data-testid="curate-edit-first"
                value={editFirst}
                onChange={(e) => setEditFirst(e.target.value)}
                placeholder="First name"
                className="bg-white border border-hg-border rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-hg-green"
              />
              <input
                data-testid="curate-edit-last"
                value={editLast}
                onChange={(e) => setEditLast(e.target.value)}
                placeholder="Last name"
                className="bg-white border border-hg-border rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-hg-green"
              />
            </div>
          </div>

          {/* Decision buttons */}
          <div className="grid grid-cols-2 gap-3">
            <button
              data-testid="curate-decide-skip"
              onClick={() => decide('skip')}
              className="flex items-center justify-center gap-2 py-3 border border-red-200 bg-red-50 text-red-700 rounded-sm hover:bg-red-100 font-sans transition-colors"
            >
              <X size={16} /> Skip
            </button>
            <button
              data-testid="curate-decide-include"
              onClick={() => decide('include')}
              className="flex items-center justify-center gap-2 py-3 bg-hg-green text-white rounded-sm hover:bg-hg-green-hover font-sans transition-colors"
            >
              <Check size={16} /> Include
            </button>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between pt-2">
            <button data-testid="curate-prev" onClick={() => go(-1)} disabled={cursorIdx === 0} className="flex items-center gap-1 text-xs text-hg-text-dim hover:text-hg-text disabled:opacity-30">
              <ChevronLeft size={12} /> Previous
            </button>
            <button data-testid="curate-next" onClick={() => go(1)} className="flex items-center gap-1 text-xs text-hg-text-dim hover:text-hg-text">
              Next <ChevronRight size={12} />
            </button>
          </div>
        </div>
      )}

      {/* Finalize bar (always visible when there are includes) */}
      {stats.included > 0 && (
        <div className="bg-hg-green/5 border border-hg-green/30 rounded-sm p-3 flex items-center justify-between">
          <div className="text-xs font-sans">
            <span className="text-hg-green font-medium">{stats.included.toLocaleString()} included</span>
            <span className="text-hg-text-dim"> — ready to push to Mailerlite</span>
          </div>
          <button data-testid="curate-open-finalize" onClick={() => setShowFinalize(true)} className="px-3 py-1.5 text-xs bg-hg-green text-white rounded-sm hover:bg-hg-green-hover inline-flex items-center gap-1.5">
            <Save size={12} /> Create Group
          </button>
        </div>
      )}
    </div>
  );
}
